import useRequest from 'shared/hooks/useRequest';
import { useMemo } from 'react';
import { QueryParams } from 'shared/types';
import { UrbanCenterWithZone } from 'shared/types';
import { ProductTarifType, TARIF_TYPE } from 'shared/constants/business.constants';
import { catalogPriceService, priceFamilyService, productService } from 'features/products/services';
import {
  CatalogPriceZoneSPPriceFamily,
  getCorrectDataTarifTypes,
  mappingCatalogPriceZoneSPForPriceFamilies,
} from './useCatalogPriceZoneServiceProvider';
import { PriceFamily } from '../models';
import { ManualProductPrices } from 'shared/types';
import { PriceFamiliesParams } from '../types';

export type MoreProductType = {
  serviceProviderId: number;
  productId: number;
  quantity?: number;
  isTransport?: boolean;
  preservedSellingPrice?: number; // Add preserved selling price to query params
};

type CatalogPriceZoneOneServiceProviderQueryParamsType = QueryParams & {
  serviceProviderZoneIds?: UrbanCenterWithZone[];
  moreProducts?: MoreProductType[];
  siteAddressFull?: string;
  manualProductPrices?: ManualProductPrices[];
  contactId?: number;
  serviceProviderId?: number;
};

export const useCatalogPriceZoneOneServiceProviderQuery = (
  query?: CatalogPriceZoneOneServiceProviderQueryParamsType,
) => {
  return useMemo(() => {
    return [{ ...query }];
  }, []);
};

export type CatalogPriceZoneSPWithProduct = {
  uuid?: string;
  productId?: number;
  productName?: string;
  priceFamilies?: CatalogPriceZoneSPPriceFamily[];
  priceValue: string | number;
  selectedPriceFamilyId?: number | null;
  quantity?: number;
  comment?: string | null;
  isTransport?: boolean;
  unitPrice?: number; // Add unitPrice
  sellingUnitPrice?: number;
  totalPrice?: number;
  preservedSellingPrice?: number; // Add preserved selling price
  isWasteTreatment?: boolean;
  orderCreatedAt?: string | null;
  isManualPrice?: boolean;
  catalogPriceValue?: string;
  wasInitiallyManual?: boolean;
};

export type CombineCatalogPriceLineZoneOneServiceProvider = {
  serviceProviderId: number;
  products: CatalogPriceZoneSPWithProduct[];
};

/**
 * Custom hook to fetch catalog price zone for one service provider.
 *
 * @param query - Query parameters to fetch catalog price zone for one service provider.
 * @returns A request object with the fetched data.
 */
export const useCatalogPriceZoneOneServiceProvider = (query: CatalogPriceZoneOneServiceProviderQueryParamsType) => {
  /**
   * Fetches catalog price zone for one service provider and associated data.
   *
   * @param options - Optional query parameters to merge with the original query.
   * @returns A promise resolving to an array of combined catalog price line zone one service provider data.
   */
  const action = async (options?: CatalogPriceZoneOneServiceProviderQueryParamsType) => {
    try {
      const mergedQuery = { ...query, ...options };
      const { serviceProviderZoneIds = [], moreProducts = [], serviceProviderId } = mergedQuery;
      const moreProductIds = moreProducts.map((p) => ({ productId: p.productId }));

      // Case: SP outside the zone
      if (!serviceProviderZoneIds.length && !serviceProviderId && moreProducts.length > 0) {
        const productIds = new Set<number>();

        // Add productIds from productMap array
        if (moreProductIds && moreProductIds.length > 0) {
          moreProductIds.forEach((p) => {
            if (p.productId != null) productIds.add(p.productId);
          });
        }

        // Fetch data of products and price families
        const { productsData, priceFamiliesData } = await fetchProductsAndFamilies(productIds, mergedQuery);

        // Mapping service providers with their products and price families
        const result: CombineCatalogPriceLineZoneOneServiceProvider[] = moreProducts.map(
          ({ serviceProviderId: spId }) => {
            // Get products by serviceProviderId
            const productsList = productsData.rows.map((product) => {
              // Get price families by productTypeId
              const priceFamilies = priceFamiliesData.filter((pf) => pf.productTypeId === product?.productTypeId);
              // Get manual price for this product and service provider if exists
              const manualPrice = query.manualProductPrices?.find(
                (manual) => manual.productId === product?.id && manual.serviceProviderId === spId,
              );
              // Mapping product with their tarifs
              const mappedPriceFamilies = mappingProductWithTheirTarifs(
                priceFamilies,
                product?.id,
                spId,
                query.manualProductPrices,
              );
              const priceValue = manualPrice?.price ?? '';
              const selectedPriceFamilyId = null;
              const isManualPrice = !!manualPrice;
              const orderCreatedAt = manualPrice?.updatedAt ? new Date(manualPrice.updatedAt).toISOString() : null;

              return {
                productId: product?.id,
                productName: product?.name,
                priceFamilies: mappedPriceFamilies,
                priceValue,
                selectedPriceFamilyId,
                isManualPrice,
                quantity: 1,
                comment: '',
                isTransport: product?.isTransport,
                isWasteTreatment: product.isWasteTreatment ?? false,
                unitPrice: 0, // Initialize unitPrice
                totalPrice: 0,
                orderCreatedAt,
              };
            });

            return {
              serviceProviderId: spId,
              products: productsList,
            };
          },
        );

        return result;
      }

      // Return early if no or moreProducts are provided
      if (!moreProducts.length) return [];

      // Fetch catalog price zone for one service provider from the service
      const zoneIdsArr = serviceProviderZoneIds.map(({ zoneId }) => zoneId);
      const requestData = {
        serviceProviderZoneIdFulls: serviceProviderZoneIds,
        serviceProviderZoneIds: zoneIdsArr,
        products: [...moreProductIds],
        ...(mergedQuery.siteAddressFull ? { siteAddressFull: mergedQuery.siteAddressFull } : {}),
        ...(mergedQuery.contactId ? { contactId: mergedQuery.contactId } : {}),
      };

      const response = await catalogPriceService.getOneCatalogPriceZoneServiceProvider(requestData);

      const {
        product: productResult,
        serviceProviderCatalogPriceWithPrice: catalogEntry,
        manualProductPrices = [],
      } = response;

      // Add productIds from productResult
      const productIds = new Set<number>();
      if (productResult && productResult.productId != null) {
        productIds.add(productResult.productId);
      }

      // Fetch data of products and price families
      const { productsData, priceFamiliesData } = await fetchProductsAndFamilies(productIds, mergedQuery);

      // Add quantity and serviceProviderId from moreProducts
      const moreMatched = moreProducts.find((item) => item.productId === productResult.productId);
      const productMap = {
        productId: productResult.productId,
        quantity: moreMatched?.quantity ?? 1,
        serviceProviderId: moreMatched?.serviceProviderId,
        isTransport: moreMatched?.isTransport ?? true,
      };

      // Mapping service providers with their products and price families
      const result: CombineCatalogPriceLineZoneOneServiceProvider[] = moreProducts.map(
        ({ serviceProviderId: spId, preservedSellingPrice }) => {
          const productsList: CatalogPriceZoneSPWithProduct[] = [];
          const { productId } = productMap;
          const product = productsData?.rows.find((prod) => prod.id === productId);

          let priceFamilies: CatalogPriceZoneSPPriceFamily[] = [];
          let priceValue: string | number = '';
          let selectedPriceFamilyId = null;
          let comment = '';
          let orderCreatedAt = null;
          let isManualPrice = false;
          if (priceFamiliesData && priceFamiliesData.length > 0) {
            // Find the price families
            const filteredPriceFamilies = priceFamiliesData.filter((pf) => pf.productTypeId === product?.productTypeId);
            // Map product catalog price zone with its price families
            priceFamilies = mappingCatalogPriceZoneSPForPriceFamilies({
              serviceProviderId: spId,
              productId: productMap.productId,
              priceFamilies: filteredPriceFamilies,
              catalogPriceZoneSP: catalogEntry,
              manualProductPrices: manualProductPrices,
            });

            // Do not automatically select the first family
            selectedPriceFamilyId = null;
          } else {
            const generalTarifType = TARIF_TYPE.GENERAL as ProductTarifType;
            // Get the correct tarif types
            const correctTarifList = getCorrectDataTarifTypes(generalTarifType, catalogEntry);
            // Handles for the case - no tarif details
            const catalogPriceForProduct = correctTarifList.find(
              (e) => e.productId === product?.id && Number(e.serviceProviderId) === spId,
            );
            const manualPrice = manualProductPrices?.find(
              (mp) => mp.productId === product?.id && mp.serviceProviderId === spId,
            );
            // Get the price value and comment
            priceValue = catalogPriceForProduct?.priceValue ?? manualPrice?.price ?? '';
            if (!catalogPriceForProduct?.priceValue && manualPrice?.price) {
              isManualPrice = true;
            }
            orderCreatedAt =
              catalogPriceForProduct?.orderCreatedAt ??
              (manualPrice?.updatedAt ? new Date(manualPrice.updatedAt).toISOString() : undefined);
            comment = catalogPriceForProduct?.comment ?? '';
          }
          // Map product with its price families
          const catalogPriceZoneWithProduct = {
            productId: product?.id,
            productName: product?.name ?? '',
            priceFamilies,
            priceValue,
            selectedPriceFamilyId,
            isManualPrice,
            quantity: productMap?.quantity ?? 1,
            comment,
            isTransport: product?.isTransport ?? true,
            isWasteTreatment: product?.isWasteTreatment ?? false,
            serviceProviderPrice: priceValue,
            unitPrice: 0, // Initialize unitPrice
            totalPrice: preservedSellingPrice ?? 0, // Use preserved selling price if available
            preservedSellingPrice, // Store preserved selling price
            orderCreatedAt,
          };

          // Push product with its price families
          productsList.push(catalogPriceZoneWithProduct);

          return {
            serviceProviderId: spId,
            products: productsList,
          };
        },
      );
      return result;
    } catch (error) {
      console.error('useCatalogPriceZoneOneServiceProvider error:', error);
      return [];
    }
  };

  return useRequest<CombineCatalogPriceLineZoneOneServiceProvider[]>({
    action,
    params: query,
    default: [],
  });
};

/**
 * Fetches products and their price families from the API.
 * @param productIds - A set of product IDs to fetch.
 * @param query - Optional query parameters to include in the price families API call.
 * @returns A promise resolving to an object containing the products and their price families.
 */
const fetchProductsAndFamilies = async (
  productIds: Set<number>,
  mergedQuery: CatalogPriceZoneOneServiceProviderQueryParamsType = {},
) => {
  const productIdsArray = [...productIds];

  // Fetch the products
  const productsData = await productService.getProductsByIds({
    // The API expects an array of product IDs as a string
    'id[]': JSON.stringify(productIdsArray),
    limit: 'unlimited',
  });

  // Get the product type IDs from the fetched products
  const productTypeIds = productsData?.rows.map((p) => p.productTypeId);

  // Prepare the price families request params
  const priceFamiliesParams: PriceFamiliesParams = {
    // The API expects an array of product type IDs as a string
    'productTypeId[]': JSON.stringify(productTypeIds),
    // Include the prices with their options and type logic
    include: 'Prices.PriceOptions|Prices.PriceTypeLogic',
  };

  // Add additional query parameters if they exist
  console.log(mergedQuery, 'mergedQuery');
  if (Object.keys(mergedQuery).length > 0) {
    if (mergedQuery.contactId) priceFamiliesParams.contactId = mergedQuery.contactId;
    if (mergedQuery.siteAddressFull) priceFamiliesParams.siteAddressFull = mergedQuery.siteAddressFull;
    if (mergedQuery.serviceProviderId) priceFamiliesParams.serviceProviderId = mergedQuery.serviceProviderId;

    // Use the first product ID from the products array if available
    const firstProduct = productsData?.rows?.[0];
    if (firstProduct?.id) priceFamiliesParams.productId = firstProduct.id;
  }
  // Fetch the price families for the product types
  const priceFamiliesData = await priceFamilyService.getPriceFamilies(priceFamiliesParams);
  return { productsData, priceFamiliesData };
};

/**
 * Maps the price families with their prices and price options.
 * @param priceFamilies - An array of price families to be mapped.
 * @param productId - The ID of the product.
 * @param serviceProviderId - The ID of the service provider.
 * @param manualProductPrices - Optional array of manual product prices.
 * @returns An array of price families with their prices and price options mapped.
 */
const mappingProductWithTheirTarifs = (
  priceFamilies: PriceFamily[],
  productId?: number,
  serviceProviderId?: number,
  manualProductPrices?: ManualProductPrices[],
): CatalogPriceZoneSPPriceFamily[] => {
  // Map price families
  const result = priceFamilies.map((pf) => {
    // Map prices
    const prices = pf.Prices?.map((p) => {
      // Map price options
      const priceOptions = p.PriceOptions?.map((po) => {
        // Find manual price for this option
        const manualOptionPrice = manualProductPrices?.find(
          (m) =>
            m.productId === productId &&
            m.serviceProviderId === serviceProviderId &&
            m.priceFamilyId === pf.id &&
            m.priceId === p.id &&
            m.priceOptionId === po.id,
        );

        const catalogPriceValue = po?.defaultValue ?? '';
        const priceValue = manualOptionPrice?.price ?? catalogPriceValue;
        const isManual = !!manualOptionPrice;
        const orderCreatedAt = manualOptionPrice?.updatedAt
          ? new Date(manualOptionPrice.updatedAt).toISOString()
          : null;

        return {
          ...po,
          priceValue,
          catalogPriceValue,
          isManualPrice: isManual,
          wasInitiallyManual: isManual,
          comment: '',
          orderCreatedAt,
        };
      });
      let orderCreatedAt: string | null = null;
      const catalogPriceValue = p?.defaultPrice?.toString() ?? '';
      // Use the defaultPrice as the price value if it exists, otherwise use an empty string
      let priceValue = catalogPriceValue;

      // Only check for manual price if default price is not available
      let isManualPrice = false;
      let wasInitiallyManual = false;
      if (!priceValue && productId && serviceProviderId && manualProductPrices) {
        const manualPrice = manualProductPrices.find(
          (manual) =>
            manual.productId === productId &&
            manual.serviceProviderId === serviceProviderId &&
            manual.priceFamilyId === pf.id &&
            manual.priceId === p.id &&
            (manual.priceOptionId === null || manual.priceOptionId === undefined),
        );
        if (manualPrice) {
          priceValue = manualPrice.price;
          isManualPrice = true;
          wasInitiallyManual = true;
          orderCreatedAt = manualPrice.updatedAt ? new Date(manualPrice.updatedAt).toISOString() : null;
        }
      }
      const isPriceManual = isManualPrice || priceOptions?.some((opt) => opt.isManualPrice);

      return {
        ...p,
        priceValue,
        catalogPriceValue,
        isManualPrice: isPriceManual,
        wasInitiallyManual,
        comment: '',
        priceOptions,
        orderCreatedAt,
      };
    });
    // Check for manual price at the price family level
    let orderCreatedAt: string | null = null;
    const catalogPriceValue = '';
    let priceFamilyValue = '';
    let isFamilyManualPrice = false;
    let wasInitiallyManual = false;
    if (productId && serviceProviderId && manualProductPrices) {
      const manualPrice = manualProductPrices.find(
        (manual) =>
          manual.productId === productId &&
          manual.serviceProviderId === serviceProviderId &&
          manual.priceFamilyId === pf.id,
      );
      if (manualPrice) {
        priceFamilyValue = manualPrice.price;
        isFamilyManualPrice = true;
        wasInitiallyManual = true;
        orderCreatedAt = manualPrice.updatedAt ? new Date(manualPrice.updatedAt).toISOString() : null;
      }
    }

    const isHolisticFamilyManual = isFamilyManualPrice || prices?.some((p) => p.isManualPrice);

    return {
      ...pf,
      serviceProviderId: serviceProviderId ?? 0,
      priceValue: priceFamilyValue,
      catalogPriceValue,
      isManualPrice: isHolisticFamilyManual,
      wasInitiallyManual: wasInitiallyManual,
      comment: '',
      prices,
      orderCreatedAt,
    };
  });
  return result;
};
